import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, RefreshCw, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { GeneratePdfMenu } from '@/components/pdf-generation/GeneratePdfMenu'
import { Button } from '@/components/ui/button'
import { caseKeys } from '@/hooks/use-case-queries'
import { api } from '@/services/api'
import { cn } from '@/utils/cn'

function RefreshCounts({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const refresh = useMutation({
    mutationFn: () => api.cases.refreshCounts(caseId),
    onSuccess: () => {
      toast.success(t('overview.refreshCountsSuccess'))
      void client.invalidateQueries({ queryKey: caseKeys.detail(caseId) })
      void client.invalidateQueries({ queryKey: caseKeys.list })
    },
    onError: () => toast.error(t('overview.refreshCountsError')),
  })
  return (
    <Button
      variant="outline"
      size="icon-sm"
      onClick={() => refresh.mutate()}
      disabled={refresh.isPending}
      aria-label={t('overview.refreshCounts')}
      title={t('overview.refreshCounts')}
    >
      <RefreshCw className={cn('size-4', refresh.isPending && 'animate-spin')} />
    </Button>
  )
}

export function CaseOverviewActions({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <>
      <RefreshCounts caseId={caseId} />
      <GeneratePdfMenu subjectType="case" subjectRef={caseId} caseId={caseId} variant="outline" />
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(`/cases/${caseId}/documents?upload=1`)}
      >
        <Upload className="size-4" />
        {t('overview.uploadDocument')}
      </Button>
      <Button variant="outline" size="sm" onClick={() => navigate(`/cases/${caseId}/settings`)}>
        <Pencil className="size-4" />
        {t('common.edit')}
      </Button>
    </>
  )
}
